import React, { useEffect, useState } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  Alert, 
  message, 
  Row, 
  Col, 
  Space, 
  Divider,
  Progress,
  Avatar,
  Badge
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  TeamOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  GlobalOutlined,
  BankOutlined
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

import { useAuth } from '../../../contexts/AuthContext';
import useCreateClientSelfOnboarding from '../../../api/CreateClientSelfOnboarding';
import { useCountryDetection } from '../../../hooks/useCountryDetection';

const { Title, Text } = Typography;

const ClientProfileCompletion = () => {
  const [form] = Form.useForm();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [disableEmail, setDisableEmail] = useState(false);
  const [disablePhone, setDisablePhone] = useState(false);
  const history = useHistory();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const { countryCode } = useCountryDetection();
  
  const { createClient, response, error: apiError, isLoading: submitting } = useCreateClientSelfOnboarding();

  useEffect(() => {
    if (user && isAuthenticated) {
      // Pre-fill the form with user data from AuthContext
      const userName = user.name || 
        (user.given_name && user.family_name ? `${user.given_name} ${user.family_name}` : '') ||
        user.email?.split('@')[0] || '';
      
      // Disable individual fields if they already exist in profile
      const hasEmail = user.email && user.email.trim() !== '';
      const hasPhone = user.phone_number && user.phone_number.trim() !== '';

      setDisableEmail(!!hasEmail);
      setDisablePhone(!!hasPhone);
      
      form.setFieldsValue({
        email: user.email || '',
        phoneNumber: user.phone_number || '',
        name: userName
      });
      setPhoneNumber(user.phone_number || '');
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [user, isAuthenticated, form]);

  // Handle successful profile creation
  useEffect(() => {
    if (response) {
      message.success('Profile created successfully!');
      // Refresh user data to update roles/profile status
      refreshUser();
      history.push('/admin');
    }
  }, [response, history]);

  // Handle API errors
  useEffect(() => {
    if (apiError) {
      const detail = apiError.response?.data?.detail;
      console.log(detail)
      if (Array.isArray(detail)) {
        // Map backend errors to form fields
        detail.forEach((err) => {
          const fieldName = err.loc?.[1]; // e.g., "phone"
          const msg = err.msg || 'Invalid value';
  
          if (fieldName) {
            form.setFields([
              {
                name: fieldName === "phone" ? "phoneNumber" : fieldName, // map API field name to your form field name
                errors: [msg],
              },
            ]);
          }
        });
      } else {
        setError(apiError.response?.data?.message || 'Failed to create profile. Please try again.');
      }
    }
  }, [apiError]);

  const handleFinish = (values) => {
    setError('');
    
    // Use the prefilled values if a contact field is disabled, otherwise use form values
    const emailToUse = disableEmail ? (user?.email || values.email) : values.email;
    const phoneToUse = disablePhone ? (phoneNumber || user?.phone_number) : phoneNumber;
    
    // Create client profile using the API
    createClient(
      values.name,
      emailToUse,
      phoneToUse,
      values.organizationName,
      values.websiteUrl
    );
  };

  // Show loading state while fetching user data
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <Card 
          style={{ 
            maxWidth: 400, 
            textAlign: 'center',
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            border: 'none'
          }}
        >
          <div style={{ padding: '40px 20px' }}>
            <LoadingOutlined style={{ fontSize: '48px', color: '#667eea', marginBottom: '20px' }} />
            <Title level={4} style={{ color: '#333', marginBottom: '8px' }}>
              Setting up your profile
            </Title>
            <Text type="secondary">
              Please wait while we load your information...
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <Alert 
          type="error" 
          message={error} 
          style={{ 
            maxWidth: 500, 
            borderRadius: '12px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }} 
        />
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          /* Modern Input Styling */
          .modern-input {
            border: 2px solid #e8e8e8 !important;
            border-radius: 12px !important;
            padding: 12px 16px !important;
            font-size: 16px !important;
            transition: all 0.3s ease !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.06) !important;
            background: #fff !important;
          }
          
          .modern-input:hover {
            border-color: #d9d9d9 !important;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
          }
          
          .modern-input:focus,
          .modern-input.ant-input-focused {
            border-color: #667eea !important;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
            outline: none !important;
          }
          
          /* Modern Phone Input Styling */
          .modern-phone-input {
            border: 2px solid #e8e8e8 !important;
            border-radius: 12px !important;
            transition: all 0.3s ease !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.06) !important;
            background: #fff !important;
            height: 48px !important;
            display: flex !important;
            align-items: center !important;
            padding: 0 12px !important;
          }
          
          .modern-phone-input:hover {
            border-color: #d9d9d9 !important;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
          }
          
          .modern-phone-input:focus-within {
            border-color: #667eea !important;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
          }
          
          .modern-phone-input .PhoneInputCountry {
            margin-right: 8px !important;
            display: flex !important;
            align-items: center !important;
          }
          
          .modern-phone-input .PhoneInputCountrySelect {
            border: none !important;
            background: transparent !important;
            padding: 0 !important;
            margin: 0 !important;
            height: auto !important;
          }
          
          .modern-phone-input .PhoneInputCountryIcon {
            width: 20px !important;
            height: 15px !important;
            border-radius: 2px !important;
            margin-right: 4px !important;
          }
          
          .modern-phone-input .PhoneInputCountrySelectArrow {
            color: #667eea !important;
            margin-left: 4px !important;
          }
          
          .modern-phone-input input {
            font-size: 16px !important;
            color: #333 !important;
            border: none !important;
            box-shadow: none !important;
            background: transparent !important;
            flex: 1 !important;
            height: auto !important;
            padding: 0 !important;
          }
          
          .modern-phone-input input:focus {
            outline: none !important;
            box-shadow: none !important;
          }
        `}
      </style>
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Header Section */}
          <Card 
            style={{ 
              marginBottom: '24px',
              borderRadius: '16px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              border: 'none',
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                  <Avatar 
                    size={80} 
                    icon={<BankOutlined />} 
                    style={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      marginBottom: '16px'
                    }} 
                  />
                  <Title level={2} style={{ margin: '0 0 8px 0', color: '#333' }}>
                    Complete Your Client Profile
                  </Title>
                  {user && (
                    <Text type="secondary" style={{ fontSize: '16px' }}>
                      Welcome back, <strong>{user.name || user.email}</strong>! Let&apos;s get your organization set up.
                    </Text>
                  )}
                </div>
                
                <Progress 
                  percent={70} 
                  strokeColor={{
                    '0%': '#667eea',
                    '100%': '#764ba2',
                  }}
                  style={{ maxWidth: '300px', margin: '0 auto' }}
                />
                
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  Just a few more details to complete your client profile
                </Text>
              </Space>
            </div>
          </Card>

          {/* Form Section */}
          <Card 
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              border: 'none',
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleFinish}
              style={{ padding: '20px' }}
            >
              {/* Personal Information Section */}
              <div style={{ marginBottom: '32px' }}>
                <Title level={4} style={{ color: '#333', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                  <UserOutlined style={{ marginRight: '8px', color: '#667eea' }} />
                  Personal Information
                </Title>
                
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item 
                      label={
                        <Space>
                          <MailOutlined style={{ color: '#667eea' }} />
                          Email Address
                          {disableEmail && <Badge status="success" text="Verified" />}
                        </Space>
                      }
                      name="email"
                      rules={disableEmail ? [] : [
                        { required: true, message: 'Please enter your email' },
                        { type: 'email', message: 'Please enter a valid email address' },
                      ]}
                    >
                      <Input 
                        disabled={disableEmail} 
                        placeholder={disableEmail ? "Your verified email" : "Enter your email"}
                        size="large"
                        className="modern-input"
                      />
                    </Form.Item>
                  </Col>
                  
                  <Col xs={24} md={12}>
                    <Form.Item 
                      label={
                        <Space>
                          <PhoneOutlined style={{ color: '#667eea' }} />
                          Phone Number
                          {disablePhone && <Badge status="success" text="Verified" />}
                        </Space>
                      }
                      name="phoneNumber"
                      rules={disablePhone ? [] : [
                        { required: true, message: 'Please enter your phone number' },
                        {
                          validator: () => {
                            if (!phoneNumber) {
                              return Promise.reject(new Error('Please enter a valid phone number'));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <PhoneInput
                        placeholder={disablePhone ? "Your verified phone number" : "Enter your phone number"}
                        value={phoneNumber}
                        onChange={disablePhone ? undefined : setPhoneNumber}
                        disabled={disablePhone}
                        defaultCountry={countryCode}
                        international
                        countryCallingCodeEditable={false}
                        className="modern-phone-input"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={
                        <Space>
                          <UserOutlined style={{ color: '#667eea' }} />
                          Full Name
                        </Space>
                      }
                      name="name"
                      rules={[{ required: true, message: 'Please enter your name' }]}
                    >
                      <Input 
                        placeholder="Enter your full name" 
                        size="large"
                        className="modern-input"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <Divider style={{ margin: '32px 0' }} />

              {/* Organization Information Section */}
              <div style={{ marginBottom: '32px' }}>
                <Title level={4} style={{ color: '#333', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                  <BankOutlined style={{ marginRight: '8px', color: '#667eea' }} />
                  Organization Information
                </Title>
                
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={
                        <Space>
                          <TeamOutlined style={{ color: '#667eea' }} />
                          Organization Name
                        </Space>
                      }
                      name="organizationName"
                      rules={[{ required: true, message: 'Please enter your organization name' }]}
                    >
                      <Input 
                        placeholder="Enter your organization name" 
                        size="large"
                        className="modern-input"
                      />
                    </Form.Item>
                  </Col>
                  
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={
                        <Space>
                          <GlobalOutlined style={{ color: '#667eea' }} />
                          Website URL
                        </Space>
                      }
                      name="websiteUrl"
                      rules={[{ required: false, message: 'Please enter your website URL' }]}
                    >
                      <Input 
                        placeholder="Enter your website URL (optional)" 
                        size="large"
                        className="modern-input"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Submit Button */}
              <div style={{ textAlign: 'center', marginTop: '40px', paddingTop: '24px', borderTop: '1px solid #f0f0f0' }}>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={submitting} 
                  size="large"
                  style={{ 
                    minWidth: '200px',
                    height: '48px',
                    borderRadius: '24px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    fontSize: '16px',
                    fontWeight: '600',
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.3)';
                  }}
                >
                  {submitting ? (
                    <Space>
                      <LoadingOutlined />
                      Creating Profile...
                    </Space>
                  ) : (
                    <Space>
                      <CheckCircleOutlined />
                      Complete Profile
                    </Space>
                  )}
                </Button>
                
                <div style={{ marginTop: '16px' }}>
                  <Text type="secondary" style={{ fontSize: '14px' }}>
                    By completing this form, you agree to our terms of service and privacy policy
                  </Text>
                </div>
              </div>
            </Form>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ClientProfileCompletion; 